import React from 'react';
import { useNavigate } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';

interface AdminTile {
  title: string;
  description: string;
  path: string;
  emoji: string;
  // Tiles whose route is AdminRoute-gated: hide from non-admins so a Manager
  // never sees a tile that would 403 into a dead page.
  adminOnly?: boolean;
  // feat-251: same intent as `adminOnly`, generalized. Some tiles are reachable
  // by Admin AND Corporate but not Manager, so `adminOnly` is the wrong shape
  // for them — it would also hide the tile from Corporate. Trivia is that case:
  // every route in the backend's `routes/admin_trivia.py` is corporate-only and
  // stays that way (Trivia is explicitly excluded from the feat-251 manager
  // curriculum grant), so a Manager clicking it lands on a 403. Normalized
  // role names, as produced by `normalizeRole` below.
  hideFromRoles?: string[];
}

const normalizeRole = (raw: string | undefined | null): string | null =>
  raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : null;

const TILES: AdminTile[] = [
  {
    title: 'Learn Module',
    description: 'Edit units, lessons, questions, and rehearsals that appear on the Flutter Learn screen.',
    path: '/quiz-management',
    emoji: '📚',
  },
  {
    title: 'Trivia',
    description: 'Short lore questions shown as a pop-up when a server opens the app. No XP or scorecard effect.',
    path: '/trivia-management',
    emoji: '🎲',
    hideFromRoles: ['Manager'],
  },
  {
    // feat-338 — the analysis routes reject the manager role (403), and the page
    // is behind CorporateAdminRoute. Third, after Trivia: keeps the Learn tiles together.
    title: 'Question Analysis',
    description:
      'Which Learn questions people miss, get stuck on, or take too long on, and how lesson time compares by store.',
    path: '/restaurant/administration/question-analysis',
    emoji: '🔎',
    hideFromRoles: ['Manager'],
  },
  {
    title: 'Phrase Guidelines',
    description: 'Positive and negative phrases the AI watches for in server transcripts.',
    path: '/phrase-rules',
    emoji: '💬',
  },
  {
    title: 'Send a Notification',
    description: 'Compose a one-off notification to servers or managers at this location.',
    path: '/restaurant/administration/notifications',
    emoji: '🔔',
  },
  {
    title: 'Locations',
    description: 'Per-location settings, including the Square POS connection.',
    path: '/admin/locations',
    emoji: '📍',
  },
];

const Administration: React.FC = () => {
  const navigate = useNavigate();
  const { tenantName, selectedTenant } = useTenant();
  const { user } = useAuth();

  const role = normalizeRole(user?.managementUser?.role);
  const isAdmin = role === 'Admin';
  const visibleTiles = TILES.filter(
    (tile) =>
      (!tile.adminOnly || isAdmin) &&
      !(role !== null && (tile.hideFromRoles ?? []).includes(role)),
  );

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-display font-semibold text-ink mb-1">Administration</h1>
          <p className="text-sm text-inkMuted">
            Configure settings and content for{' '}
            <span className="font-semibold text-ink">{tenantName}</span>.
          </p>
          {!selectedTenant && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              No tenant selected. Return to a scorecard page and pick a tenant first.
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleTiles.map((tile) => (
            <button
              key={tile.path}
              onClick={() => navigate(tile.path)}
              className="text-left bg-white border border-softBorder rounded-xl p-5 shadow-sm hover:shadow-md hover:border-coral/40 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-coral to-gold flex items-center justify-center text-2xl mb-3">
                {tile.emoji}
              </div>
              <h2 className="text-lg font-display font-semibold text-ink mb-1">{tile.title}</h2>
              <p className="text-sm text-inkMuted">{tile.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Administration;
